import { englishCanonicalUrl } from "@/config/canonical";
import { arrow } from "@/designSystemIcons";
import { mixpanelTrack } from "@/events/mixpanelTrack";
import { useGoBack } from "@/hooks/useGoBack";
import {
  STRAPI_URL,
  extractYouTube,
  imageSrcFromBlock,
  imgUrl,
  mdToHtml,
  visibilityFilter,
} from "@/integrations/strapi-cms/utils";
import {
  Article,
  Block,
  formatArticleDate,
} from "@/integrations/strapi-cms/utils";
import axios from "axios";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useParams } from "react-router";

const ArticlePage = () => {
  const { slug: slugOrId } = useParams();
  // Most readers arrive from search, with no in-app entry behind them.
  const goBack = useGoBack({ fallback: "/news" });
  const [article, setArticle] = useState<Article | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const environmentFilter = visibilityFilter(window.location.hostname);

        const identifierFilter =
          Number.isFinite(Number(slugOrId)) &&
          `${Number(slugOrId)}` === slugOrId
            ? `&filters[id][$eq]=${Number(slugOrId)}`
            : `&filters[slug][$eq]=${encodeURIComponent(slugOrId || "")}`;

        const articlesApiUrl =
          `${STRAPI_URL}/api/articles` +
          `?publicationState=live` +
          identifierFilter +
          environmentFilter +
          `&populate[cover]=true` +
          `&populate[author]=true` +
          `&populate[category]=true` +
          `&populate[blocks][on][shared.media][populate]=file` +
          `&populate[blocks][on][shared.rich-text]=*` +
          `&populate[blocks][on][shared.quote]=*`;

        const res = await axios.get<{ data: Article[] }>(articlesApiUrl);
        const article = res.data?.data?.[0] ?? null;
        setArticle(article);
        if (article) {
          mixpanelTrack("news_article_viewed", {
            slug: article.slug,
            title: article.title,
          });
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, [slugOrId]);

  if (loading)
    return (
      <div className="h-screen flex items-center justify-center w-full bg-panel-base" />
    );

  if (!article)
    return (
      <div className="h-screen flex items-center justify-center w-full bg-panel-base">
        Article not found
      </div>
    );

  const cover = imgUrl(article.cover);

  const Breadcrumb = ({ title }: { title: string }) => (
    <div className="h-10 flex w-full items-center justify-center bg-black/20">
      <div className="flex flex-row items-center w-full max-w-content">
        <div className="flex px-4 py-3 cursor-pointer" onClick={goBack}>
          <div className="w-3 rotate-90 text-dark-tertiary">{arrow}</div>
        </div>
        <h2 className="text-dark-secondary text-xs mobile:text-base truncate">
          {title}
        </h2>
      </div>
    </div>
  );

  return (
    <div className="bg-panel-base min-h-screen">
      <Helmet>
        {/* React 19 hoists <title> itself and drops it unless children is one string. */}
        <title>{`${article.title} – EasySBC`}</title>
        <meta
          name="description"
          content={article.description ?? article.title}
        />
        <meta property="og:title" content={article.title} />
        {article.cover?.url && (
          <meta property="og:image" content={article.cover.url} />
        )}
        <meta
          property="og:description"
          content={article.description ?? article.title}
        />
        {/* No hreflang: the article is English under every prefix (DEV-3684). */}
        {/* Trailing slash: prerendered articles live at /news/<slug>/ (S3 302s the bare path). */}
        <link
          rel="canonical"
          href={`${englishCanonicalUrl(`/news/${article.slug}`)}/`}
        />
      </Helmet>
      <Breadcrumb title={article.title} />
      <article className="w-full mx-auto py-8 max-w-6xl px-4 sm:px-6 lg:px-12">
        <h1 className="font-bold mb-2 text-xl mobile:text-3xl">
          {article.title}
        </h1>

        {article.description && (
          <p className="mb-6 text-sm md:text-base">{article.description}</p>
        )}

        {cover && (
          <img
            src={cover}
            alt={article.cover?.alternativeText || article.title}
            className="w-full object-cover mb-6 max-h-[28rem]"
          />
        )}

        <div className="flex gap-1 text-sm border-t-2 border-b-2 border-panel-background py-2">
          {article.author?.name && <span>{article.author.name},</span>}
          {article.publishedAt && (
            <span>{formatArticleDate(article.publishedAt)}</span>
          )}
        </div>

        <div className="flex flex-col space-y-6 mt-6">
          {article.blocks?.map((block: Block) => {
            if (block.__component === "shared.rich-text" && "body" in block) {
              const { embed, md } = extractYouTube(block.body || "");
              const html = mdToHtml(md || "");

              return (
                <div key={block.id}>
                  {embed && (
                    <div className="relative aspect-video max-w-3xl mx-auto">
                      <iframe
                        src={embed}
                        title={article.title}
                        className="absolute inset-0 w-full h-full"
                        allowFullScreen
                      />
                    </div>
                  )}
                  <div
                    className="
                      text-dark-secondary
                      [&_p]:my-3
                      [&_a]:text-primary-blue [&_a]:underline
                      [&_ul]:list-disc [&_ul]:pl-6
                      [&_ol]:list-decimal [&_ol]:pl-6
                      [&_li]:my-1
                      [&_h1]:text-2xl [&_h1]:font-bold [&_h1]:mt-6
                      [&_h2]:text-xl [&_h2]:font-bold [&_h2]:mt-5
                      [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:mt-4
                      [&_blockquote]:border-l-4 [&_blockquote]:pl-4 [&_blockquote]:italic
                      [&_code]:bg-black/10 [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:rounded
                    "
                    dangerouslySetInnerHTML={{ __html: html }}
                  />
                </div>
              );
            }

            if (block.__component === "shared.media") {
              const src = imageSrcFromBlock(block);
              return src ? (
                <div key={block.id} className="flex justify-center">
                  <img
                    src={src}
                    alt=""
                    className="w-full max-w-xl object-cover rounded my-4"
                  />
                </div>
              ) : null;
            }
            return null;
          })}
        </div>
      </article>
    </div>
  );
};

export default ArticlePage;
